import { Box, Button, HStack, Tag } from "@chakra-ui/react";
import MarkDownRender from "./MarkDownRender";
import Banner from "./elements/Banner";
import Quiz from "./elements/Quiz/Quiz";
import Defination from "./elements/Defination";
import PythonCodeEditor from "./elements/PythonCodeEditor";
import Stepper from "./elements/Stepper";
import TabsButtons from "./elements/TabsButtons";
import RepeatButton from "./elements/RepeatButton";
import { memo, useCallback, useEffect, useRef } from "react";
import Image from "./elements/Image";
import DragAndDropQuiz from "./elements/Quiz/DragAndDropQuiz";
import AdditionalNote from "./elements/AdditionalNote";
import Group from "./elements/Group";
import HtmlCodeEditor from "./elements/HtmlCodeEditor";
import { useAppDispatch, useAppSelector } from "../store";
import { useParams } from "react-router-dom";
import { useAddProgress } from "../core/Module/api";
import { EventType } from "../core/Module";
import { showContinue, updateProgress } from "../store/slices/contentSlice";
import CodeEditor from "./elements/CodeEditor";
import FeedbackQuiz from "./elements/FeedbackQuiz";
import { ModuleType } from "../internal/courses/api";
import PluginComponent from "./elements/PluginComponent";

type IMeta = Record<any, any>;

export type LessonClickEvent =
  | "click"
  | "add"
  | "add-below"
  | "remove"
  | "add-plugin"
  | "practice-edit"
  | "change-plugin-props";

export type IContentAPI = {
  hint_viewed: (meta?: IMeta) => void;
  explanation_viewed: (meta?: IMeta) => void;
  completed: (meta?: IMeta) => void;
  attempted: (meta?: IMeta) => void;
  answer_revealed: (meta?: IMeta) => void;
};

type IJsonRenderProps = {
  content: IConfig[];
  onClick?: (contentId: number, event: LessonClickEvent) => void;
  mode:
    | "view"
    | "edit"
    | "selection"
    | "learn"
    | "practice-edit"
    | "practice-view";
  width?: string;
  lessonId?: string;
  practiceId?: string;
  lessonType?: ModuleType;
};

const visibleStyle = {
  opacity: 1,
  height: "auto",
  width: "auto",
  overflow: "visible",
  transition: "all 0.4s ease",
};

const hiddenStyle = {
  opacity: 0,
  height: 0,
  width: 0,
  overflow: "hidden",
  transition: "all 0.4s ease",
};

const JsonRender = memo(
  ({
    content,
    onClick,
    mode = "view",
    width,
    lessonId,
    practiceId,
    lessonType = ModuleType.content,
  }: IJsonRenderProps) => {
    const { revealIndex } = useAppSelector((s) => s.content);
    const dispatch = useAppDispatch();
    const { courseId = "", moduleId = "", mode: viewMode } = useParams();
    const addProgress = useAddProgress({
      course: courseId,
      lesson: lessonId,
      practice: practiceId,
      module: moduleId,
    });
    const elementRefs = useRef<(HTMLDivElement | null)[]>([]);

    useEffect(() => {
      if (elementRefs.current[revealIndex] && viewMode === "learn") {
        elementRefs.current[revealIndex]?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, [revealIndex]);

    const contentAPI = (
      contentId: number,
      isViewComponent: boolean = true
    ) => ({
      hint_viewed: (meta?: IMeta) =>
        addProgress.mutate({
          event: {
            content: contentId,
            isViewComponent,
            eventType: EventType.HINT_VIEWED,
            meta: meta ?? {},
          },
        }),
      explanation_viewed: (meta?: IMeta) => {
        addProgress.mutate({
          event: {
            content: contentId,
            isViewComponent,
            eventType: EventType.EXPLANATION_VIEWED,
            meta: meta ?? {},
          },
        });
        dispatch(showContinue());
        if (mode === "practice-view") {
          dispatch(updateProgress());
        }
      },
      completed: (meta?: IMeta) => {
        addProgress.mutate({
          event: {
            isViewComponent,
            content: contentId,
            eventType: EventType.COMPLETE,
            meta: meta ?? {},
          },
        });
        dispatch(showContinue());
        if (mode === "practice-view") {
          dispatch(updateProgress());
        }
      },
      attempted: (meta?: IMeta) => {
        addProgress.mutate({
          event: {
            isViewComponent,
            content: contentId,
            eventType: EventType.ATTEMPT,
            meta: meta ?? {},
          },
        });
      },
      answer_revealed: (meta?: IMeta) => {
        addProgress.mutate({
          event: {
            isViewComponent,
            content: contentId,
            eventType: EventType.ANSWER_REVEALED,
            meta: meta ?? {},
          },
        });
      },
    });

    const getVisibilityStyle = useCallback(
      (idx: number) => {
        if (mode === "learn") {
          return idx <= revealIndex ? visibleStyle : hiddenStyle;
        } else if (mode === "practice-view") {
          return idx === revealIndex ? visibleStyle : hiddenStyle;
        } else {
          return visibleStyle;
        }
      },
      [revealIndex, mode]
    );

    return (
      <Box mx={"auto"} mb={viewMode === "learn" ? ".5rem" : "0rem"}>
        {content.map((c, idx) => (
          <Box
            ref={(el) => (elementRefs.current[idx] = el)}
            scrollMarginTop={{ base: "3rem", md: "8rem" }}
            mb={
              mode === "selection"
                ? "4rem"
                : idx > revealIndex - 1
                ? "0rem"
                : mode === "view"
                ? "0rem"
                : "3rem"
            }
            p={
              mode === "selection"
                ? "1rem"
                : mode === "edit"
                ? "1rem"
                : mode === "view"
                ? "0rem"
                : "0rem"
            }
            border={mode === "selection" ? "1px solid" : "none"}
            borderColor={"neutral.800"}
            sx={getVisibilityStyle(idx)}
            key={idx}
            pb={{ base: mode === "view" ? "0rem" : "2rem", md: "0" }}
          >
            {mode === "edit" && (
              <HStack mb={".5rem"}>
                <Tag
                  cursor={"pointer"}
                  onClick={() => {
                    if (onClick) onClick(idx, "click");
                  }}
                  colorScheme="blue"
                >
                  {idx}-Change
                </Tag>
                <Tag
                  cursor={"pointer"}
                  onClick={() => {
                    if (onClick) onClick(idx, "add");
                  }}
                  colorScheme="blue"
                >
                  Add above
                </Tag>
                <Tag
                  cursor={"pointer"}
                  onClick={() => {
                    if (onClick) onClick(idx, "add-below");
                  }}
                  colorScheme="blue"
                >
                  Add below
                </Tag>
                <Tag
                  cursor={"pointer"}
                  onClick={() => {
                    if (onClick) onClick(idx, "add-plugin");
                  }}
                  colorScheme="blue"
                >
                  Add Plugin
                </Tag>
                <Tag
                  cursor={"pointer"}
                  onClick={() => {
                    if (onClick) onClick(idx, "remove");
                  }}
                  colorScheme="red"
                >
                  Remove
                </Tag>

                <Tag
                  cursor={"pointer"}
                  onClick={() => {
                    if (onClick) onClick(idx, "change-plugin-props");
                  }}
                  colorScheme="green"
                >
                  Change Props
                </Tag>
              </HStack>
            )}
            <Box maxW={width ?? "33rem"} mx={"auto"}>
              {c.component === "Banner" && <Banner {...c.props} />}
              {c.component === "Markdown" && (
                <Box px={"1.5rem"}>
                  <MarkDownRender {...c.props} />
                </Box>
              )}
              {c.component === "PythonCodeEditor" && (
                <PythonCodeEditor {...c.props} />
              )}
              {c.component === "Stepper" && <Stepper {...c.props} />}
              {c.component === "Defination" && <Defination {...c.props} />}
              {c.component === "Tabs" && <TabsButtons {...c.props} />}
              {c.component === "Repeat" && <RepeatButton {...c.props} />}
              {c.component === "Image" && <Image {...c.props} />}
              {/* quizez */}
              {c.component === "DragAndDropQuiz" && (
                <DragAndDropQuiz
                  {...c.props}
                  contentAPI={contentAPI(idx)}
                  moduleType={lessonType}
                />
              )}
              {c.component === "Quiz" && (
                <Quiz
                  {...c.props}
                  contentAPI={contentAPI(idx)}
                  moduleType={lessonType}
                />
              )}
              {c.component === "FeedbackQuiz" && (
                <FeedbackQuiz {...c.props} contentAPI={contentAPI(idx)} />
              )}
              {/* end of quizes */}
              {c.component === "AdditionalNote" && (
                <AdditionalNote {...c.props} />
              )}
              {c.component === "Group" && <Group {...c.props} />}
              {c.component === "HtmlCodeEditor" && (
                <HtmlCodeEditor {...c.props} />
              )}
              {c.component === "PluginComponent" && (
                <PluginComponent {...c.props} />
              )}
            </Box>

            {c.component === "CodeEditor" && (
              <CodeEditor
                {...c.props}
                showExplanation
                componentLevel={mode === "learn"}
              />
            )}

            {mode === "selection" && (
              <Button
                mt={"1rem"}
                onClick={() => {
                  if (onClick) onClick(idx, "click");
                }}
                variant={"solid"}
                colorScheme="green"
              >
                Select
              </Button>
            )}
          </Box>
        ))}
      </Box>
    );
  }
);

export default JsonRender;
